import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Radio, Server, GraduationCap, ArrowRight, CheckCircle } from 'lucide-react';
import { services, serviceImages } from '../mock';

const Services = () => {
  const serviceIcons = {
    telecoms: Radio,
    informatique: Server,
    formations: GraduationCap
  };

  const serviceColors = {
    telecoms: "bg-blue-50 border-blue-200 hover:bg-blue-100",
    informatique: "bg-orange-50 border-orange-200 hover:bg-orange-100", 
    formations: "bg-slate-50 border-slate-200 hover:bg-slate-100"
  };

  const iconColors = {
    telecoms: "text-blue-600",
    informatique: "text-orange-600",
    formations: "text-slate-600"
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Nos Expertises
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            Services & Solutions
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez notre gamme complète de services en télécommunications, 
            informatique et formation professionnelle.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {Object.entries(services).map(([key, service]) => {
            const Icon = serviceIcons[key];
            return (
              <Card 
                key={key} 
                className={`group ${serviceColors[key]} transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer border-2 overflow-hidden`}
              >
                {/* Service Image */}
                {service.image && (
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={`${service.title} - ${service.description} par SET-G en Côte d'Ivoire`}
                      title={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                        <Icon className={`w-6 h-6 ${iconColors[key]}`} />
                      </div>
                    </div>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  {/* Removed items check */}
                  {!service.image && (
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-md group-hover:shadow-lg transition-all duration-300">
                      <Icon className={`w-8 h-8 ${iconColors[key]}`} />
                    </div>
                  )}
                  <CardTitle className="text-2xl font-bold text-slate-900 group-hover:text-slate-800 transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-slate-600 text-lg leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3 mb-6">
                    {service.items.slice(0, 4).map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full group border-slate-300 hover:border-slate-400 hover:bg-white transition-all duration-200"
                    onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    En savoir plus
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Calo to Action */}
        <div className="bg-slate-900 rounded-2xl p-8 lg:p-12 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Besoin d'une Solution Personnalisée ?
          </h3>
          <p className="text-slate-300 text-lg mb-8 max-w-3xl mx-auto">
            Nos experts analysent vos besoins spécifiques et conçoivent des solutions 
            sur mesure pour optimiser vos performances.
          </p>
          <Button 
            size="lg"
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Demander un Devis
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;